import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL;
export const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL;

const STORAGE_KEY = 'concordare_app_banner_dismissed';

export default function MobileAppBanner() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === '1'
  );

  if (Capacitor.isNativePlatform() || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  const storeButtons = (
    <>
      <Button
        component="a"
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        variant="outlined"
        startIcon={<AndroidIcon />}
        sx={{
          borderColor: 'currentColor',
          color: 'inherit',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          px: 1.25,
          '&:hover': { borderColor: 'currentColor', bgcolor: 'rgba(255,255,255,0.15)' },
        }}
      >
        Google Play
      </Button>
      <Button
        component="a"
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        variant="outlined"
        startIcon={<PhoneIphoneIcon />}
        sx={{
          borderColor: 'currentColor',
          color: 'inherit',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          px: 1.25,
          '&:hover': { borderColor: 'currentColor', bgcolor: 'rgba(255,255,255,0.15)' },
        }}
      >
        App Store
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1300,
          p: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {t('mobileApp.bannerTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('mobileApp.bannerSubtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Button
              component="a"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              startIcon={<AndroidIcon />}
              sx={{ fontSize: '0.75rem', px: 1.25 }}
            >
              Google Play
            </Button>
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              startIcon={<PhoneIphoneIcon />}
              sx={{ fontSize: '0.75rem', px: 1.25 }}
            >
              App Store
            </Button>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleDismiss} aria-label={t('common.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 2,
        py: 0.75,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
        {t('mobileApp.bannerText')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {storeButtons}
      </Box>
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label={t('common.close')}
        sx={{
          color: 'inherit',
          position: 'absolute',
          right: 8,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
